/**
 * Framio Studio — client-side frame compositor.
 *
 * Everything runs in the browser: the user's photo is composited under a
 * transparent frame on a <canvas>, with pan / zoom / rotate / flip, background
 * fill and image adjustments, then exported at HD without ever hitting a server.
 */
import Alpine from 'alpinejs';

const SIZE = 1080; // internal export resolution (square)

Alpine.data('framioStudio', (config = {}) => ({
  cfg: config,

  // image + frame
  img: null,
  frameImg: null,
  frameReady: false,
  baseScale: 1,

  // transform
  scale: 1,
  rotation: 0,
  flipX: 1,
  offsetX: 0,
  offsetY: 0,

  // background + adjustments
  bgMode: 'color',
  bgColor: '#ffffff',
  brightness: 100,
  contrast: 100,
  saturation: 100,
  swatches: ['#ffffff', '#000000', '#B22222', '#D4AF37', '#1A1A1A', '#2563EB', '#F5F5F5', '#0F766E'],

  // ui state
  photoLoaded: false,
  dragging: false,
  busy: false,

  // builder (upload-your-own-frame) wizard state
  step: 3,             // 1=upload frame · 2=detail · 3=studio (builder only)
  frameChosen: false,
  frameBusy: false,
  frameError: '',
  frameDragging: false,
  frameName: '',       // uploaded frame file name (display)
  frameFile: null,     // retained File for publishing
  customName: '',
  customCaption: '',

  // publish-to-gallery flow
  showAuthModal: false,
  showPublishModal: false,
  showSuccessModal: false,
  publishing: false,
  published: false,
  publishName: '',
  publishDesc: '',
  publishCategory: 'komunitas',
  publishError: '',
  publishedUrl: '',
  publishedName: '',
  creatorName: '',

  // pointer tracking
  _pointers: new Map(),
  _dragStart: null,
  _pinchStart: null,

  init() {
    this.ctx = this.$refs.canvas.getContext('2d');
    this.$refs.canvas.width = SIZE;
    this.$refs.canvas.height = SIZE;

    this.isBuilder = this.cfg.mode === 'builder';
    this.step = this.isBuilder ? 1 : 3;

    if (this.cfg.frameUrl) {
      this.loadFrameFromUrl(this.cfg.frameUrl, true);
    }

    this.draw();

    if (this.isBuilder) this._restoreDraft();
  },

  loadFrameFromUrl(url, crossOrigin = false) {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => { this.frameImg = img; this.frameReady = true; this.draw(); };
    img.onerror = () => { this.frameReady = false; this.draw(); };
    img.src = url;
  },

  get shouldWatermark() {
    return !!(this.cfg.watermark || this.cfg.isPremium);
  },

  get waUrl() {
    const text = `${this.cfg.shareText} ${window.location.href}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  },

  // ---- file handling ---------------------------------------------------
  pickFile() { this.$refs.file.click(); },

  onFile(e) {
    const file = e.target.files?.[0];
    if (file) this.loadFile(file);
  },

  onDrop(e) {
    this.dragging = false;
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) this.loadFile(file);
  },

  loadFile(file) {
    if (file.size > 15 * 1024 * 1024) {
      window.toast?.('Ukuran foto maksimal 15 MB.', 'error');
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      this.img = img;
      this.baseScale = Math.max(SIZE / img.width, SIZE / img.height);
      this.resetTransform();
      this.photoLoaded = true;
      URL.revokeObjectURL(url);
      this.draw();
    };
    img.onerror = () => window.toast?.('Foto gagal dimuat. Coba file lain.', 'error');
    img.src = url;
  },

  // ---- builder: upload your own frame ---------------------------------
  pickFrame() { this.$refs.frameFile?.click(); },

  onFrameFile(e) {
    const file = e.target.files?.[0];
    if (file) this.validateFrame(file);
    e.target.value = '';
  },

  onFrameDrop(e) {
    this.frameDragging = false;
    const file = e.dataTransfer?.files?.[0];
    if (file) this.validateFrame(file);
  },

  async validateFrame(file) {
    this.frameError = '';
    this.frameBusy = true;
    try {
      const isPng = file.type === 'image/png' || /\.png$/i.test(file.name);
      if (!isPng) {
        throw new Error('Format bingkai harus PNG (.png).');
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Ukuran bingkai maksimal 5 MB.');
      }

      const url = URL.createObjectURL(file);
      const img = await this._loadImage(url);

      if (!this._hasTransparency(img)) {
        URL.revokeObjectURL(url);
        throw new Error('Bingkai wajib punya area transparan sebagai tempat foto. Ekspor ulang PNG dengan latar transparan.');
      }

      this.frameImg = img;
      this.frameReady = true;
      this.frameChosen = true;
      this.frameName = file.name;
      this.frameFile = file;
      this.cfg.frameUrl = url; // keep as blob URL for this session
      if (!this.customName) this.customName = file.name.replace(/\.png$/i, '').replace(/[-_]+/g, ' ').trim();
      this.draw();
    } catch (err) {
      this.frameError = err.message || 'Bingkai tidak valid.';
    } finally {
      this.frameBusy = false;
    }
  },

  _loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Gambar gagal dimuat.'));
      img.src = url;
    });
  },

  // Detects whether the PNG contains a meaningful transparent region.
  _hasTransparency(img) {
    const S = 200;
    const c = document.createElement('canvas');
    c.width = S; c.height = S;
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.drawImage(img, 0, 0, S, S);
    let data;
    try { data = cx.getImageData(0, 0, S, S).data; } catch (_) { return true; }
    let transparent = 0;
    const total = S * S;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 200) transparent++;
    }
    return transparent / total > 0.01; // >1% of area is see-through
  },

  changeFrame() {
    this.frameChosen = false;
    this.frameReady = false;
    this.frameImg = null;
    this.frameError = '';
    this.draw();
    this.pickFrame();
  },

  goToDetail() { if (this.frameChosen) this.step = 2; },

  startEditing() {
    this.customName = (this.customName || '').trim() || 'Bingkai Kamu';
    this.cfg.name = this.customName;
    this.cfg.shareText = (this.customCaption || '').trim()
      || `Aku bikin bingkai sendiri: ${this.customName} — pakai Framio USPATIH GO!`;
    this.cfg.fileName = this._slug(this.customName) || 'bingkai-framio';
    this.step = 3;
    this.$nextTick(() => this.draw());
  },

  backTo(n) { this.step = n; },

  _slug(s) {
    return String(s).toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  },

  // ---- publish to gallery (requires login) ----------------------------
  publish() {
    if (!this.frameChosen) { window.toast?.('Unggah bingkai dulu.', 'info'); return; }
    if (this.published) return;

    if (!this.$store?.auth?.isLoggedIn) {
      this.showAuthModal = true;
      return;
    }
    this.publishError = '';
    this.publishName = this.publishName || this.customName || '';
    this.showPublishModal = true;
  },

  goLogin() { this._saveDraft(); window.location.href = this.cfg.loginUrl || '/login'; },
  goRegister() { this._saveDraft(); window.location.href = this.cfg.registerUrl || '/register'; },

  async submitPublish() {
    if (this.publishing) return;
    const name = (this.publishName || '').trim();
    if (!name) { this.publishError = 'Nama bingkai wajib diisi.'; return; }
    if (!this.frameFile) { this.publishError = 'File bingkai tidak ditemukan. Unggah ulang.'; return; }

    this.publishing = true;
    this.publishError = '';
    try {
      const fd = new FormData();
      fd.append('frame', this.frameFile);
      fd.append('name', name);
      fd.append('category', this.publishCategory || 'komunitas');
      if (this.publishDesc) fd.append('description', this.publishDesc);
      if (this.customCaption) fd.append('share_text', this.customCaption);

      const { data } = await window.axios.post(this.cfg.publishUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      this.published = true;
      this.showPublishModal = false;
      this.publishedUrl = data.url || (window.location.origin + '/framio/' + (data.slug || ''));
      this.publishedName = data.name || name;
      this.creatorName = this.$store?.auth?.user?.name || 'Kamu';
      this.showSuccessModal = true;
      this._clearDraft();
      window.toast?.(data.message || 'Bingkai berhasil dipublikasikan!', 'success');
    } catch (e) {
      if (e.response?.status === 401) {
        this.showPublishModal = false;
        this.showAuthModal = true;
      } else {
        this.publishError = e.response?.data?.message
          || Object.values(e.response?.data?.errors || {})?.[0]?.[0]
          || 'Gagal mempublikasikan. Coba lagi.';
      }
    } finally {
      this.publishing = false;
    }
  },

  shareCaption() {
    return `${this.cfg.shareText || this.publishedName} ${this.publishedUrl}`.trim();
  },

  copyPublishedLink() {
    navigator.clipboard?.writeText(this.publishedUrl)
      .then(() => window.toast?.('Link disalin ke clipboard.', 'success'))
      .catch(() => window.toast?.('Gagal menyalin link.', 'error'));
  },

  // ---- draft persistence across login redirect -------------------------
  async _saveDraft() {
    try {
      sessionStorage.setItem('framio_publish_intent', '1');
      if (!this.frameFile) return;
      const dataUrl = await this._fileToDataURL(this.frameFile);
      if (dataUrl && dataUrl.length < 4_500_000) {
        sessionStorage.setItem('framio_draft', JSON.stringify({
          frame: dataUrl,
          name: this.customName || this.publishName || '',
          caption: this.customCaption || '',
        }));
      }
    } catch (_) { /* storage full — user re-uploads after login */ }
  },

  _clearDraft() {
    try {
      sessionStorage.removeItem('framio_draft');
      sessionStorage.removeItem('framio_publish_intent');
    } catch (_) {}
  },

  _restoreDraft() {
    let raw = null, intent = null;
    try {
      raw = sessionStorage.getItem('framio_draft');
      intent = sessionStorage.getItem('framio_publish_intent');
    } catch (_) { return; }
    if (!raw) { this._clearDraft(); return; }

    try {
      const d = JSON.parse(raw);
      this.customName = d.name || '';
      this.customCaption = d.caption || '';
      const file = this._dataURLtoFile(d.frame, (this._slug(d.name) || 'bingkai') + '.png');
      this.validateFrame(file).then(() => {
        this.step = 3;
        this.$nextTick(() => this.draw());
        // Returned from login with a pending publish intent → publish immediately
        // and show the shareable link, no extra clicks.
        if (intent && this.$store?.auth?.isLoggedIn && this.frameChosen) {
          this.publishName = this.customName || this.frameName || 'Bingkai Kamu';
          this.publishCategory = this.publishCategory || 'komunitas';
          this.$nextTick(() => this.submitPublish());
        }
      });
    } catch (_) { /* ignore malformed draft */ }
    this._clearDraft();
  },

  _fileToDataURL(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  },

  _dataURLtoFile(dataUrl, name) {
    const [head, body] = dataUrl.split(',');
    const mime = (head.match(/:(.*?);/) || [])[1] || 'image/png';
    const bin = atob(body);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new File([arr], name, { type: mime });
  },

  // ---- transforms ------------------------------------------------------
  rotate90() { this.rotation = ((this.rotation + 90 + 180) % 360) - 180; this.draw(); },
  flip() { this.flipX *= -1; this.draw(); },

  resetTransform() {
    this.scale = 1; this.rotation = 0; this.flipX = 1;
    this.offsetX = 0; this.offsetY = 0;
    this.brightness = 100; this.contrast = 100; this.saturation = 100;
    this.draw();
  },

  setBg(mode) { this.bgMode = mode; this.draw(); },
  setColor(c) { this.bgColor = c; this.bgMode = 'color'; this.draw(); },

  // ---- pointer interactions -------------------------------------------
  _toCanvas(e) {
    const r = this.$refs.canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (SIZE / r.width), y: (e.clientY - r.top) * (SIZE / r.height) };
  },

  onDown(e) {
    if (!this.photoLoaded) return;
    this.$refs.canvas.setPointerCapture?.(e.pointerId);
    this._pointers.set(e.pointerId, this._toCanvas(e));
    if (this._pointers.size === 1) {
      const p = this._toCanvas(e);
      this._dragStart = { x: p.x, y: p.y, ox: this.offsetX, oy: this.offsetY };
    } else if (this._pointers.size === 2) {
      this._pinchStart = { dist: this._pointerDist(), scale: this.scale };
    }
  },

  onMove(e) {
    if (!this.photoLoaded || !this._pointers.has(e.pointerId)) return;
    this._pointers.set(e.pointerId, this._toCanvas(e));

    if (this._pointers.size >= 2 && this._pinchStart) {
      const ratio = this._pointerDist() / this._pinchStart.dist;
      this.scale = Math.min(4, Math.max(0.3, this._pinchStart.scale * ratio));
      this.draw();
      return;
    }
    if (this._dragStart) {
      const p = this._toCanvas(e);
      this.offsetX = this._dragStart.ox + (p.x - this._dragStart.x);
      this.offsetY = this._dragStart.oy + (p.y - this._dragStart.y);
      this.draw();
    }
  },

  onUp(e) {
    this._pointers.delete(e.pointerId);
    if (this._pointers.size < 2) this._pinchStart = null;
    if (this._pointers.size === 0) this._dragStart = null;
  },

  onWheel(e) {
    if (!this.photoLoaded) return;
    const delta = -Math.sign(e.deltaY) * 0.08;
    this.scale = Math.min(4, Math.max(0.3, this.scale + delta));
    this.draw();
  },

  _pointerDist() {
    const pts = [...this._pointers.values()];
    if (pts.length < 2) return 1;
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
  },

  // ---- rendering -------------------------------------------------------
  draw(ctx = this.ctx) {
    if (!ctx) return;
    ctx.clearRect(0, 0, SIZE, SIZE);

    // background
    if (this.bgMode === 'color') {
      ctx.fillStyle = this.bgColor;
      ctx.fillRect(0, 0, SIZE, SIZE);
    } else if (this.bgMode === 'blur' && this.img) {
      ctx.save();
      ctx.filter = 'blur(26px)';
      const bs = Math.max(SIZE / this.img.width, SIZE / this.img.height) * 1.25;
      const dw = this.img.width * bs, dh = this.img.height * bs;
      ctx.drawImage(this.img, (SIZE - dw) / 2, (SIZE - dh) / 2, dw, dh);
      ctx.restore();
      ctx.filter = 'none';
    }

    // photo
    if (this.img) {
      ctx.save();
      ctx.translate(SIZE / 2 + this.offsetX, SIZE / 2 + this.offsetY);
      ctx.rotate((this.rotation * Math.PI) / 180);
      ctx.scale(this.flipX * this.scale * this.baseScale, this.scale * this.baseScale);
      ctx.filter = `brightness(${this.brightness}%) contrast(${this.contrast}%) saturate(${this.saturation}%)`;
      ctx.drawImage(this.img, -this.img.width / 2, -this.img.height / 2);
      ctx.restore();
      ctx.filter = 'none';
    }

    // frame overlay
    if (this.frameReady) {
      ctx.drawImage(this.frameImg, 0, 0, SIZE, SIZE);
    }

    // watermark
    if (this.shouldWatermark && this.img) {
      ctx.save();
      ctx.globalAlpha = 0.32;
      ctx.font = '700 34px Arial, sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.fillStyle = '#ffffff';
      ctx.strokeText('USPATIH GO', SIZE - 30, SIZE - 26);
      ctx.fillText('USPATIH GO', SIZE - 30, SIZE - 26);
      ctx.restore();
    }
  },

  // ---- export ----------------------------------------------------------
  async _toBlob() {
    const transparent = this.bgMode === 'transparent';
    const type = transparent ? 'image/png' : 'image/jpeg';
    // Re-render onto a clean offscreen canvas at full resolution.
    const off = document.createElement('canvas');
    off.width = SIZE; off.height = SIZE;
    this.draw(off.getContext('2d'));
    return await new Promise((res) => off.toBlob(res, type, 0.94));
  },

  async download() {
    if (!this.photoLoaded || this.busy) return;
    this.busy = true;
    try {
      const blob = await this._toBlob();
      const ext = this.bgMode === 'transparent' ? 'png' : 'jpg';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${this.cfg.fileName || 'framio'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      window.toast?.('Bingkai berhasil diunduh!', 'success');
      this._track();
    } catch (err) {
      window.toast?.('Gagal menyimpan gambar. Coba lagi.', 'error');
    } finally {
      this.busy = false;
    }
  },

  async nativeShare() {
    if (!this.photoLoaded) { window.toast?.('Unggah foto dulu.', 'info'); return; }
    try {
      const blob = await this._toBlob();
      const ext = this.bgMode === 'transparent' ? 'png' : 'jpg';
      const file = new File([blob], `${this.cfg.fileName || 'framio'}.${ext}`, { type: blob.type });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: this.cfg.shareText });
        this._track();
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: this.cfg.name, text: this.cfg.shareText, url: window.location.href });
        return;
      }
      this.copyLink();
    } catch (_) { /* user cancelled */ }
  },

  copyLink() {
    navigator.clipboard?.writeText(window.location.href)
      .then(() => window.toast?.('Link disalin ke clipboard.', 'success'))
      .catch(() => window.toast?.('Gagal menyalin link.', 'error'));
  },

  _track() {
    if (!this.cfg.trackUrl) return;
    try { window.axios?.post(this.cfg.trackUrl).catch(() => {}); } catch (_) {}
  },
}));
